import React, { useRef, useState, useEffect } from "react";
import { AnimatePhaseContext } from "./AnimatePhaseContext";
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";

interface AnimatePresenceProps {
  children: React.ReactNode;
  /** Use shorter exit duration for popovers (120ms vs 200ms for dialogs) */
  popover?: boolean;
}

function hasContent(children: React.ReactNode): boolean {
  return React.Children.toArray(children).length > 0;
}

const AnimatePresence: React.FC<AnimatePresenceProps> = ({
  children,
  popover = false,
}) => {
  const exitDuration = popover ? 120 : 200;
  const childrenPresent = hasContent(children);

  const [phase, setPhase] = useState<"enter" | "exit">("enter");
  const [shouldRender, setShouldRender] = useState(childrenPresent);
  const lastChildrenRef = useRef<React.ReactNode>(children);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  if (childrenPresent) {
    lastChildrenRef.current = children;
  }

  // Enter/exit state machine. Keyed on `childrenPresent` alone, deliberately:
  //
  // `shouldRender` is this effect's own output, not an input. Listing it would
  // be harmless today — the extra run it triggers (when the exit timer flips
  // it to false) finds `childrenPresent` false and no-ops — but it would make
  // the effect's correctness depend on every branch staying idempotent under
  // its own writes, which is a much easier invariant to break than the one
  // being avoided. Keyed on the input only, the state machine has exactly one
  // trigger.
  //
  // `exitDuration` is derived from `popover`, which is a static literal at
  // every call site (`<AnimatePresence popover>` or not), so it cannot change
  // for a mounted instance. Listing it would only add a way to restart an
  // in-flight exit timer that nothing can actually trigger.
  useEffect(() => {
    if (childrenPresent) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setShouldRender(true);
      setPhase("enter");
    } else if (shouldRender) {
      setPhase("exit");
      // Read the preference here rather than at mount: this component instance
      // outlives any number of open/close cycles, and the OS setting can be
      // toggled between them.
      const duration = prefersReducedMotion() ? 0 : exitDuration;
      if (duration === 0) {
        setShouldRender(false);
        setPhase("enter");
      } else {
        timeoutRef.current = setTimeout(() => {
          setShouldRender(false);
          setPhase("enter");
        }, duration);
      }
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the note above the effect: `shouldRender` is this effect's own output, and `exitDuration` is fixed for the lifetime of an instance
  }, [childrenPresent]);

  if (!shouldRender) return null;

  return (
    <AnimatePhaseContext.Provider value={phase}>
      <div data-animate-phase={phase} style={{ display: "contents" }}>
        {childrenPresent ? children : lastChildrenRef.current}
      </div>
    </AnimatePhaseContext.Provider>
  );
};

export default AnimatePresence;
